import { OperatorValue, QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { isRuleValid } from '../../../../../utils/queryBuilderUtils';
import { Translate, parseSubscriptionSelections, toStringList } from '../ValueEditors/valueUtils';
import {
    COUNTER_TYPE_FIELD_NAME,
    DUAL_LIST_FIELD_NAMES,
    GEOGRAPHY_FIELD_NAMES,
    OPTIN_TO_STATUS_FIELD_NAME,
    PURCHASE_PERIOD_FIELD_NAME,
    STATUS_FIELD_NAMES,
    resolveFieldLabel,
} from './fieldConfig';
import { resolveFieldValues } from './fieldValues';
import { channelPriorityLabels, parseChannelPriorityValues } from './channelPriority';

export interface SummaryField {
    id: string;
    label?: string;
    intro?: string;
    values: string[];
    secondIntro?: string;
    secondValues?: string[];
}

interface SummaryContext {
    field: QueryField;
    rule: QueryRule;
    t: Translate;
    formatLocale: string;
    fields: QueryField[];
    rules: QueryRule[];
}

type SummaryBuilder = (ctx: SummaryContext) => SummaryField | null;

const SIMPLE_KEY_PREFIX = 'pages.clients.queryBuilder.simple';

const OPERATOR_LABEL_KEY: Partial<Record<OperatorValue, string>> = {
    '!=': 'notEquals',
    '>': 'greaterThan',
    '<': 'lessThan',
    '>=': 'greaterOrEqual',
    '<=': 'lessOrEqual',
    between: 'between',
    in: 'in',
    'not in': 'notIn',
    contains: 'contains',
    'not contains': 'notContains',
};

const OPTIN_SUMMARY_KEY: Record<string, string> = {
    CUSTOMER_OPTIN_EMAIL: 'optinEmail',
    CUSTOMER_OPTIN_SMS: 'optinSms',
    CUSTOMER_OPTIN_COURRIER: 'optinCourrier',
};

export const HIDDEN_FROM_SUMMARY_FIELD_NAMES = new Set([COUNTER_TYPE_FIELD_NAME, PURCHASE_PERIOD_FIELD_NAME]);

const summaryText = (t: Translate, key: string, params?: Record<string, string | number>) =>
    t(`${SIMPLE_KEY_PREFIX}.summary.${key}`, params);

const unitLabel = (t: Translate, unit: 'store' | 'purchase', count: number) =>
    t(`${SIMPLE_KEY_PREFIX}.units.${unit}${count > 1 ? 's' : ''}`);

function uniqueLabels(field: QueryField, values: string[], t: Translate): string[] {
    const options = resolveFieldValues(field, t);
    const labelOf = (value: string) => options.find(option => option.name === value || option.mergedNames?.includes(value))?.label ?? value;
    return Array.from(new Set(values.map(labelOf)));
}

const parseBetween = (rule: QueryRule): [string, string] => {
    const raw = String(rule.value ?? '');
    if (!raw.includes('|')) return [raw, String(rule.valueTo ?? '')];
    const [min, max] = raw.split('|');
    return [min, max];
};

const formatDateValue = (raw: string, formatLocale: string): string => {
    if (!raw) return '?';
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? raw : date.toLocaleDateString(formatLocale);
};

const formatDateRange = (rule: QueryRule, formatLocale: string) => {
    const [min, max] = parseBetween(rule);
    return { min: formatDateValue(min, formatLocale), max: formatDateValue(max, formatLocale) };
};

const sentence = (rule: QueryRule, text: string): SummaryField => ({ id: rule.id, values: [text] });

function genericSummary({ field, rule, t }: SummaryContext): SummaryField {
    const operatorKey = rule.operator ? OPERATOR_LABEL_KEY[rule.operator] : undefined;
    const operator = operatorKey ? `${t(`pages.clients.queryBuilder.operators.${operatorKey}`)} ` : '';

    let value: string;
    if (rule.operator === 'between') {
        const [min, max] = parseBetween(rule);
        value = summaryText(t, 'betweenValue', { min: min || '?', max: max || '?' });
    } else if (field.valueEditorType === 'checkbox' && !Array.isArray(rule.value) && !field.values?.length) {
        value = summaryText(t, 'yes');
    } else {
        value = uniqueLabels(field, toStringList(rule.value), t).join(', ');
    }

    return sentence(rule, summaryText(t, 'item', { field: resolveFieldLabel(field, t), operator, value }));
}

function listSummary({ field, rule, t }: SummaryContext, intro: string, withLabel = true): SummaryField {
    return {
        id: rule.id,
        label: withLabel ? resolveFieldLabel(field, t) : undefined,
        intro,
        values: uniqueLabels(field, toStringList(rule.value), t),
    };
}

const plainListSummary: SummaryBuilder = ctx => listSummary(ctx, '');

const livesInSummary: SummaryBuilder = ctx =>
    listSummary(ctx, summaryText(ctx.t, ctx.rule.operator === 'not in' ? 'doesNotLiveIn' : 'livesIn'));

const dualListSummary: SummaryBuilder = ({ field, rule, t }) => {
    const includeValues = uniqueLabels(field, toStringList(rule.value), t);
    const excludeValues = uniqueLabels(field, rule.notValue ?? [], t);
    return {
        id: rule.id,
        label: resolveFieldLabel(field, t),
        intro: includeValues.length > 0 ? summaryText(t, 'belongsToClients') : undefined,
        values: includeValues,
        secondIntro: excludeValues.length > 0 ? summaryText(t, 'doesNotBelongToClients') : undefined,
        secondValues: excludeValues,
    };
};

const optinSummary = (key: string): SummaryBuilder => ({ rule, t }) =>
    sentence(rule, summaryText(t, String(rule.value) === '0' ? `${key}Refuse` : key));

const subscriptionSummary: SummaryBuilder = ctx => {
    const { field, rule, t } = ctx;
    if (!field.values?.length) {
        return listSummary(ctx, summaryText(t, rule.operator === 'not in' ? 'doesNotBelongToCategory' : 'belongsToCategory'));
    }
    const selectionByName = parseSubscriptionSelections(toStringList(rule.value));
    const sentences = field.values
        .filter(option => selectionByName.has(option.name))
        .map(option => {
            const selection = selectionByName.get(option.name);
            const key = selection === '1' ? 'subscribed' : selection === '0' ? 'unsubscribed' : 'noResponse';
            return `${option.label} : ${t(`${SIMPLE_KEY_PREFIX}.subscription.${key}`)}`;
        });
    return sentences.length > 0 ? { id: rule.id, values: sentences } : null;
};

const fieldValueLabel = (fields: QueryField[], rules: QueryRule[], fieldName: string, t: Translate): string => {
    const field = fields.find(f => f.name === fieldName);
    const rule = rules.find(r => r.field === fieldName);
    if (!field || !rule || !isRuleValid(rule)) return '';
    return uniqueLabels(field, toStringList(rule.value), t).join(', ');
};

const counterSummary: SummaryBuilder = ({ rule, t, fields, rules }) => {
    if (rule.operator !== 'between') return null;
    const [min, max] = parseBetween(rule);
    if (!min && !max) return null;
    const accountType = fieldValueLabel(fields, rules, COUNTER_TYPE_FIELD_NAME, t) || summaryText(t, 'genericCounterType');
    const key = min && max ? 'scorePointsBetween' : min ? 'scorePointsMin' : 'scorePointsMax';
    return sentence(rule, summaryText(t, key, { accountType, min, max }));
};

const purchaseCountSummary: SummaryBuilder = ({ rule, t, formatLocale, rules }) => {
    const periodRule = rules.find(r => r.field === PURCHASE_PERIOD_FIELD_NAME);
    const period = periodRule && periodRule.operator === 'between' && isRuleValid(periodRule)
        ? formatDateRange(periodRule, formatLocale)
        : undefined;

    if (rule.operator === '=' && Number(rule.value ?? -1) === 0) {
        return sentence(rule, period ? summaryText(t, 'noPurchasesBetween', period) : summaryText(t, 'noPurchases'));
    }
    if (rule.operator !== '>' && rule.operator !== '>=') return null;
    const params = { value: String(rule.value ?? ''), unit: unitLabel(t, 'purchase', Number(rule.value ?? 0)) };
    return sentence(rule, period
        ? summaryText(t, 'minPurchasesBetween', { ...params, ...period })
        : summaryText(t, 'minPurchases', params));
};

const channelPrioritySummary: SummaryBuilder = ctx => {
    const { field, rule, t } = ctx;
    const priority = parseChannelPriorityValues(field.values).priorityOf(rule.value);
    if (!priority) return plainListSummary(ctx);
    const { channelLabel, exclusionLabel } = channelPriorityLabels(t);
    return sentence(rule, summaryText(t, 'channelPriority', {
        channel: channelLabel(priority.channel),
        excluded: exclusionLabel(priority.excluded),
    }));
};

const storeSummary: SummaryBuilder = ctx => {
    const { rule, t } = ctx;
    const unit = unitLabel(t, 'store', toStringList(rule.value).length);
    return listSummary(ctx, summaryText(t, rule.operator === 'not in' ? 'storesNotBelong' : 'storesBelong', { unit }), false);
};

const withBuilder = (names: Iterable<string>, builder: SummaryBuilder) =>
    Object.fromEntries(Array.from(names, name => [name, builder]));

const FIELD_SUMMARY_BUILDERS: Record<string, SummaryBuilder> = {
    ...withBuilder(DUAL_LIST_FIELD_NAMES, dualListSummary),
    ...withBuilder(GEOGRAPHY_FIELD_NAMES, livesInSummary),
    ...withBuilder(['CUSTOMER_SCORE', ...STATUS_FIELD_NAMES], plainListSummary),
    ...Object.fromEntries(Object.keys(OPTIN_TO_STATUS_FIELD_NAME).map(name => [name, optinSummary(OPTIN_SUMMARY_KEY[name])])),
    PERSON_ENTITY: storeSummary,
    CUSTOMER_SUBSCRIPTIONS_TYPE: subscriptionSummary,
    CUSTOMER_ACCOUNT: counterSummary,
    CUSTOMER_ACTIVATION_DATE: ({ rule, t, formatLocale }) => (rule.operator === 'between'
        ? sentence(rule, summaryText(t, 'cardOpenedBetween', formatDateRange(rule, formatLocale)))
        : null),
    CUSTOMER_SCORE_BEST: ({ rule, t }) => (rule.operator === '<'
        ? sentence(rule, summaryText(t, 'topClients', { value: String(rule.value ?? '') }))
        : null),
    TRANSACTION_NB: purchaseCountSummary,
    CHANNEL_PRIORITY: channelPrioritySummary,
};

export function buildSummaryField(field: QueryField, rule: QueryRule, t: Translate, formatLocale: string, fields: QueryField[], rules: QueryRule[]): SummaryField {
    const ctx = { field, rule, t, formatLocale, fields, rules };
    return FIELD_SUMMARY_BUILDERS[field.name]?.(ctx) ?? genericSummary(ctx);
}
